/**
 * Valores, validação e payload do cadastro de frente de obra (#365, D10): três campos — Obra,
 * Nome e Responsável. A Obra só existe na criação; a frente não muda de obra.
 */
import type {
  CreateWorkFrontInput,
  UpdateWorkFrontInput,
  WorkFrontListItem,
} from '../../types/workFront';

export const NAME_MAX_LENGTH = 120;

export interface WorkFrontFormValues {
  unitId: string;
  name: string;
  responsibleEmployeeId: string;
}

export type WorkFrontFormField = keyof WorkFrontFormValues;
export type WorkFrontFormErrors = Partial<Record<WorkFrontFormField, string>>;

export const EMPTY_VALUES: WorkFrontFormValues = { unitId: '', name: '', responsibleEmployeeId: '' };

export function valuesFromFront(front: WorkFrontListItem): WorkFrontFormValues {
  return {
    unitId: String(front.unit.id),
    name: front.name,
    responsibleEmployeeId: front.responsible ? String(front.responsible.id) : '',
  };
}

/** "Salvar e cadastrar outra" mantém a obra e limpa o nome e o responsável. */
export function valuesForNext(values: WorkFrontFormValues): WorkFrontFormValues {
  return { ...EMPTY_VALUES, unitId: values.unitId };
}

export function validateField(
  field: WorkFrontFormField,
  values: WorkFrontFormValues,
  options: { requireUnit: boolean }
): string | null {
  if (field === 'unitId') {
    return options.requireUnit && values.unitId === '' ? 'Escolha a obra.' : null;
  }
  if (field === 'name') {
    const name = values.name.trim();
    if (name === '') return 'Informe o nome da frente.';
    if (name.length > NAME_MAX_LENGTH) return `O nome pode ter até ${NAME_MAX_LENGTH} letras.`;
  }
  return null;
}

export function validateForm(
  values: WorkFrontFormValues,
  options: { requireUnit: boolean }
): WorkFrontFormErrors {
  const errors: WorkFrontFormErrors = {};
  (['unitId', 'name'] as const).forEach((field) => {
    const message = validateField(field, values, options);
    if (message) errors[field] = message;
  });
  return errors;
}

const responsibleOf = (values: WorkFrontFormValues): number | null =>
  values.responsibleEmployeeId === '' ? null : Number(values.responsibleEmployeeId);

export function buildCreatePayload(values: WorkFrontFormValues): CreateWorkFrontInput {
  return {
    unitId: Number(values.unitId),
    name: values.name.trim(),
    responsibleEmployeeId: responsibleOf(values),
  };
}

export function buildUpdatePayload(values: WorkFrontFormValues): UpdateWorkFrontInput {
  return { name: values.name.trim(), responsibleEmployeeId: responsibleOf(values) };
}
